import React from 'react';
import { object, string } from 'yup';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { useTranslation } from 'react-i18next';

import { env } from '../../config';

import FormResource from './FormResource.jsx';
import FormGroup from '~/commons/components/FormGroup.jsx';
import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import { getLang } from '../../../commons/helpers.js';

const defaults = {
  active: true,
  email: '',
  password: '',
  studentId: '',
  name: { full: { en: '', zhhk: '' } },
  schoolClass: {
    title: '',
    grade: '',
  },
  sex: '',
};

const FormStudent = ({
  school,
  student,
}) => {
  // Helper to get localized sex label
  const { t, i18n: { language } } = useTranslation();
  const lang = language === 'en' ? 'en' : 'zhhk';
  return (
  <FormResource
    {...{
      defaults: { ...defaults, school: school._id },
      schema: object({
        email: string().email().required(),
        ...(!student ? {
          password: string().required(),
        } : {}),
        schoolClass: object({
          title: string().required(),
          grade: string().required(),
        }),
      }),
      stored: student ? {
        ...student,
        school: student.school._id,
        schoolClass: pick(
          student.schoolClass,
          Object.keys(defaults.schoolClass),
        ),
      } : undefined,
      api: '/api/core/users/students',
      onSubmit: (v) => omit(v, !v.password ? 'password' : null),
    }}
  >

    <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">
      {t('Authentication').toUpperCase()}
    </h6>
    <div className="form-group">
      <label>{t('Active')}</label>
      <FormCheckGroup
        name="active"
        type="checkbox"
        label=""
        asSwitch
        disabled={!env.user.roles.includes('admin')}
      />
    </div>
    <FormGroup name="email" label={t('Email')} disabled={!env.user.roles.includes('admin')} />
    <FormGroup
      name="password"
      label={t('Password')}
      type="password"
      placeholder={
        student ? t('Leave this blank to keep the stored password') : ''
      }
    />

    <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">
      {t('Student Info').toUpperCase()}
    </h6>
    <FormGroup name="studentId" label={t('Student ID')} disabled={!env.user.roles.includes('admin')} />
    <FormGroup
      name="sex"
      as="select"
      label={t('Sex')}
    >
      <option value=""> - </option>
        {Object.entries(env.sex).map(([key, val]) => (
          <option value={key} key={key}>{getLang(val, lang)}</option>
        ))}
    </FormGroup>
    <div className="form-row">
      <div className="col">
        <FormGroup name="name.full.zhhk" label={t('Name (Chinese)')} disabled={!env.user.roles.includes('admin')} />
      </div>
      <div className="col">
        <FormGroup name="name.full.en" label={t('Name (English)')} disabled={!env.user.roles.includes('admin')} />
      </div>
    </div>

    <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">
      {t('School Class').toUpperCase()}
    </h6>
    <div className="form-row">
      <div className="col">
        <FormGroup
          name="schoolClass.title"
          label={t('Class')}
          disabled={!env.user.roles.includes('admin')}
        />
      </div>
      <div className="col">
        <FormGroup
          name="schoolClass.grade"
          as="select"
          label={t('Grade')}
          disabled={!env.user.roles.includes('admin')}
        >
          <option value=""> - </option>
          {Object.keys(env.grades)
            .filter((key) => (
              (school.stage === 'p' && key <= 6)
              || (school.stage === 's' && key > 6)
            ))
            .map((key) => (
              <option value={key} key={key}>
                {env.grades[key][language === 'en' ? 'en' : 'zhhk']}
              </option>
            ))
          }
        </FormGroup>
      </div>
    </div>
  </FormResource>
  );
};

export default FormStudent;
